"use client";

import cn from "@/lib/cn";
import { Listbox } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon } from "lucide-react";

export type TSelectDropdownItem = { id: string | number; name: string };

type TSelectDropdownProps = {
	className?: string;
	selectedItem: string;
	items: TSelectDropdownItem[];
	buttonLabel: React.ReactNode;
	setActiveItems: (value: string) => void;
	valueType?: "id" | "name";
	defaultValue?: string;
	disabled?: boolean;
};

export const SelectDropdown = ({
	items,
	className,
	buttonLabel,
	selectedItem,
	setActiveItems,
	valueType = "name",
	defaultValue,
	disabled = false,
}: TSelectDropdownProps) => {
	const buttonText =
		valueType === "id"
			? items.find((item) => item.id === selectedItem)?.name
			: selectedItem || buttonLabel;

	return (
		<Listbox
			value={selectedItem}
			onChange={setActiveItems}
			defaultValue={defaultValue}
			disabled={disabled}
		>
			<div className="relative w-full">
				<Listbox.Button
					className={cn(
						"group flex w-full items-center justify-between gap-2 rounded border border-gray-200 bg-white p-2 text-left text-sm",
						{ "cursor-not-allowed opacity-50": disabled },
						className
					)}
				>
					<span className="line-clamp-1">{buttonText}</span>

					<ChevronDownIcon className="h-5 w-5 text-blue-500 transition duration-200 group-data-[headlessui-state='open']:-rotate-180" />
				</Listbox.Button>

				<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
					{items.map((item) => (
						<Listbox.Option
							key={item.id}
							value={valueType === "name" ? item.name : item.id}
							className={({ active }) =>
								cn(
									"relative select-none px-4 py-2",
									active ? "bg-brand-primary/10 text-brand-primary" : "text-gray-900"
								)
							}
						>
							{({ selected }) => (
								<div className="flex items-center justify-between">
									<span>{item.name}</span>
									{selected ? (
										<span>
											<CheckIcon className="h-4 w-4 text-brand-primary" />
										</span>
									) : null}
								</div>
							)}
						</Listbox.Option>
					))}
				</Listbox.Options>
			</div>
		</Listbox>
	);
};
